import enMessages from './messages/en';
import jaMessages from './messages/ja';
import khMessages from './messages/km';
import { meModule } from '@/store/entityModules/me';

export type Locale = 'en' | 'ja' | 'km';
export const LANGUAGE_STORAGE_KEY = 'CHAILD_SELECTED_LANGUAGE';

const catalogs: Record<Locale, typeof enMessages> = {
  en: enMessages,
  ja: jaMessages,
  km: khMessages,
};

const parseLocale = (value: string | null | undefined): Locale | null => {
  if (value === 'ja' || value === 'en' || value === 'km') {
    return value;
  }
  return null;
};

const parseApiLanguage = (value: unknown): Locale | null => {
  if (value === 0) return 'ja';
  if (value === 1) return 'en';
  if (value === 2) return 'km';
  return null;
};

const rawLocale = (process.env.VUE_APP_LOCALE as string | undefined)?.toLowerCase();
export let locale: Locale = parseLocale(rawLocale) ?? 'en';
export let dayjsLocale: 'en' | 'ja' | 'km' = locale;

const syncLocaleFromSetting = (): Locale => {
  const me = meModule.me as (Record<string, unknown> | null);
  const settingLocale = parseApiLanguage(me?.language);
  if (settingLocale && settingLocale !== locale) {
    locale = settingLocale;
    dayjsLocale = settingLocale;
  }
  return locale;
};

export const calendarLocale = (targetLocale: Locale = syncLocaleFromSetting()): string => {
  // Vuetify locale keys are `en` | `ja` | `km` (see plugins/vuetify.ts messages)
  return targetLocale;
};

/** Current app locale synced from me.language (call inside Vue computed for reactivity via meModule). */
export const getAppLocale = (): Locale => syncLocaleFromSetting();

export const isJapaneseLocale = (targetLocale: Locale = syncLocaleFromSetting()): boolean => targetLocale === 'ja';
export const isEnglishLocale = (targetLocale: Locale = syncLocaleFromSetting()): boolean => targetLocale === 'en';
export const isKhmerLocale = (targetLocale: Locale = syncLocaleFromSetting()): boolean => targetLocale === 'km';
export const isEnglishOrKhmerLocale = (targetLocale: Locale = syncLocaleFromSetting()): boolean => {
  return targetLocale === 'en' || targetLocale === 'km';
};

const resolvePath = (path: string): unknown => {
  const messages = catalogs[syncLocaleFromSetting()];
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, messages as unknown);
};

export const t = (
  path: string,
  fallback = path,
  params: Record<string, string | number> = {},
): string => {
  const template = resolvePath(path);
  const value = typeof template === 'string' ? template : fallback;
  return Object.entries(params).reduce((message, [key, replacement]) => {
    return message.replace(new RegExp(`\\{${key}\\}`, 'g'), String(replacement));
  }, value);
};
